import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cloud, CloudOff, ExternalLink, RotateCw, Search } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField } from '../../components/ui/Field.jsx';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';

const STATUS_TONE = {
  pending: 'border-muted text-muted',
  uploading: 'border-primary text-primary',
  uploaded: 'border-success text-success',
  failed: 'border-danger text-danger',
};

const STATUS_LABEL = { pending: 'Pending', uploading: 'Uploading', uploaded: 'Uploaded', failed: 'Failed' };

function UploadRow({ row, onRetry, retrying }) {
  return (
    <div className="flex flex-col gap-1 border-b border-border py-2.5 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{row.file_name ?? row.target_path}</p>
        <p className="truncate text-xs text-muted">{row.target_path}</p>
        {row.status === 'failed' && row.last_error ? <p className="text-xs text-danger">{row.last_error}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${STATUS_TONE[row.status]}`}>
          {STATUS_LABEL[row.status] ?? row.status}
        </span>
        {row.status === 'uploaded' && row.web_view_link ? (
          <a href={row.web_view_link} target="_blank" rel="noreferrer" className="text-muted hover:text-primary" aria-label="Open in Drive">
            <ExternalLink size={16} aria-hidden="true" />
          </a>
        ) : null}
        {row.status === 'failed' ? (
          <button type="button" onClick={() => onRetry(row.id)} disabled={retrying} className="text-muted hover:text-primary disabled:opacity-50" aria-label="Retry upload">
            <RotateCw size={16} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function DriveSection() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [connectError, setConnectError] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const statusQuery = useQuery({ queryKey: ['drive', 'status'], queryFn: () => api.get('/drive/status') });
  const uploadsQuery = useQuery({
    queryKey: ['drive', 'uploads', debouncedSearch],
    queryFn: () => api.get('/drive/uploads', { query: { search: debouncedSearch || undefined } }),
  });

  const connectMutation = useMutation({
    mutationFn: () => api.post('/drive/connect'),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => setConnectError(err instanceof ApiClientError ? err.message : 'Could not start the connection.'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api.post('/drive/disconnect'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drive', 'status'] }),
  });

  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      await api.post(`/drive/uploads/${id}/retry`);
      await queryClient.invalidateQueries({ queryKey: ['drive', 'uploads'] });
    } finally {
      setRetryingId(null);
    }
  };

  const status = statusQuery.data;

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Google Drive</h2>
        <p className="mt-1 text-sm text-muted">
          Every generated PDF is uploaded here automatically once connected — invoices, gold-purchase bills, repair
          vouchers, issue/receipt vouchers, chit receipts. A slow or down connection never blocks a sale; uploads just
          queue and retry.
        </p>
      </div>

      {statusQuery.isLoading ? null : !status?.configured ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-3.5 py-3 text-sm text-warning">
          Not set up yet — a Google Cloud OAuth client is needed first. See <code>docs/SETUP.md</code> for the
          one-time steps, then fill in <code>GOOGLE_CLIENT_ID</code> / <code>GOOGLE_CLIENT_SECRET</code> in{' '}
          <code>backend/.env</code>.
        </p>
      ) : status.connected ? (
        <div className="flex items-center justify-between rounded-card border border-border p-3.5">
          <div className="flex items-center gap-2">
            <Cloud size={18} className="text-success" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium">Connected as {status.email}</p>
              {status.connectedAt ? (
                <p className="text-xs text-muted">Since {new Date(status.connectedAt).toLocaleDateString('en-IN')}</p>
              ) : null}
            </div>
          </div>
          <Button variant="ghost" size="sm" loading={disconnectMutation.isPending} onClick={() => disconnectMutation.mutate()}>
            <CloudOff size={16} aria-hidden="true" /> Disconnect
          </Button>
        </div>
      ) : (
        <div>
          <Button type="button" onClick={() => connectMutation.mutate()} loading={connectMutation.isPending}>
            <Cloud size={16} aria-hidden="true" /> Connect Google Drive
          </Button>
          {connectError ? <p className="mt-2 text-sm text-danger">{connectError}</p> : null}
        </div>
      )}

      {status?.configured ? (
        <div className="flex flex-col gap-2">
          <TextField
            label="Recent uploads"
            placeholder="Search by invoice number or document type"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            hint="Every PDF this app has generated, and where it stands with Drive."
          />
          <div className="rounded-card border border-border px-3.5">
            {uploadsQuery.isLoading ? (
              <p className="py-3 text-sm text-muted">Loading…</p>
            ) : !uploadsQuery.data?.length ? (
              <p className="py-3 text-sm text-muted">
                <Search size={14} className="mr-1 inline" aria-hidden="true" />
                No uploads yet.
              </p>
            ) : (
              uploadsQuery.data.map((row) => <UploadRow key={row.id} row={row} onRetry={handleRetry} retrying={retryingId === row.id} />)
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default DriveSection;
